import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Toaster } from 'sonner';
import ClientAssignmentsTable from './ClientAssignmentsTable';
import ClientClassesTable from './ClientClassesTable';
import MessageCenter from 'components/communication/MessageCenter';
import NotificationManager from 'components/notifications/NotificationManager';
import AssignmentSubmissionForm from 'components/modals/AssignmentSubmissionForm';
import ClassManagementForm from 'components/modals/ClassManagementForm';
import DiscordConnectionPanel from 'components/discord/DiscordConnectionPanel';
import DiscordConnectionReminder from 'components/discord/DiscordConnectionReminder';
// import ProfileSettings from 'components/profile/ProfileSettings';
import ResponsiveSidebar from 'components/ui/ResponsiveSidebar';
import StickyNavbar from 'components/ui/StickyNavbar';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import { useAutoTour } from 'hooks/useTours';
import { useAssignments } from 'hooks/useAssignments';
import { useClasses } from 'hooks/useClasses';

const ClientDashboardPage = () => {
  const { profile, user } = useAuth();
  const [activeTab, setActiveTab] = useState('assignments');
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showClassModal, setShowClassModal] = useState(false);
  const [showDiscordPanel, setShowDiscordPanel] = useState(false);
  // const [showProfileSettings, setShowProfileSettings] = useState(false);
  
  // Auto-trigger client dashboard tour for new users
  useAutoTour('client-dashboard', {
    delay: 1000,
    firstVisit: true,
    userRole: 'client'
  });
  
  const { assignments } = useAssignments({ clientId: user?.uid });
  const { classes } = useClasses({ clientId: user?.uid });

  // Calculate separate stats for assignments and classes
  const assignmentStats = useMemo(() => {
    if (!assignments) return { total: 0, pending: 0, inProgress: 0, completed: 0 };
    
    return {
      total: assignments.length,
      pending: assignments.filter(a => a.status === 'pending').length,
      inProgress: assignments.filter(a => a.status === 'in-progress').length,
      completed: assignments.filter(a => a.status === 'completed').length,
    };
  }, [assignments]);

  const classStats = useMemo(() => {
    if (!classes) return { total: 0, pending: 0, inProgress: 0, completed: 0 };
    
    return {
      total: classes.length,
      pending: classes.filter(c => c.status === 'pending').length,
      inProgress: classes.filter(c => c.status === 'in-progress').length,
      completed: classes.filter(c => c.status === 'completed').length,
    };
  }, [classes]);

  const handleTabChange = (tabId) => {
    // Handle special tabs that open modals
    // switch (tabId) {
    //   case 'profile':
    //   case 'settings':
    //     setShowProfileSettings(true);
    //     break;
    //   default:
    //     setActiveTab(tabId);
    //     break;
    // }
    setActiveTab(tabId);
  };

  const handleActionClick = (action) => {
    switch (action) {
      case 'submitAssignment':
        setShowAssignmentModal(true);
        break;
      case 'submitClass':
        setShowClassModal(true);
        break;
      case 'discordPanel':
        setShowDiscordPanel(true);
        break;
      default:
        break;
    }
  };

  const handleMessageClick = (itemType, itemId, itemTitle) => {
    // Switch to messages tab and open specific conversation
    setActiveTab('messages');
    // Store conversation details for MessageCenter to open
    sessionStorage.setItem('openConversation', JSON.stringify({
      item_type: itemType,
      item_id: itemId,
      item_title: itemTitle
    }));
  };

  return (
    <>
      <StickyNavbar />
      <ResponsiveSidebar 
      currentTab={activeTab} 
      onTabChange={handleTabChange} 
      userRole="client"
      onActionClick={handleActionClick}
    >
      {/* Content Area */}
      <div className="space-y-6" style={{padding: 'var(--ios-space-4)'}}>
        {/* Landing Page Widget - Full widget like admin dashboard */}
        <div className="ios-card-primary" style={{padding: 'var(--ios-space-4) var(--ios-space-4) var(--ios-space-5)', background: 'linear-gradient(135deg, rgba(0, 122, 255, 0.1) 0%, rgba(88, 86, 214, 0.1) 100%), var(--ios-glass-primary)'}}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500/20 rounded-2xl flex items-center justify-center flex-shrink-0" style={{borderRadius: 'var(--ios-radius-2xl)', boxShadow: 'var(--ios-shadow-sm)'}}>
                <Icon name="Home" size={20} className="text-blue-400 sm:w-[22px] sm:h-[22px]" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="ios-text-primary font-semibold text-base sm:text-lg leading-tight" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-semibold)'}}>Welcome back</h3>
                <p className="ios-text-primary font-medium text-sm leading-tight mt-0.5 truncate" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-medium)'}}>{profile?.full_name || user?.email}</p>
                <p className="ios-text-secondary text-xs sm:text-sm leading-tight mt-1" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-regular)'}}>Place orders & track progress</p>
              </div>
            </div>
            <a
              href="/"
              className="ios-button-primary px-4 py-2.5 flex items-center space-x-2 w-full sm:w-auto justify-center flex-shrink-0 text-sm font-medium"
              style={{
                background: 'var(--ios-blue)',
                borderRadius: 'var(--ios-radius-xl)',
                fontFamily: 'var(--ios-font-system)',
                fontWeight: 'var(--ios-weight-semibold)',
                transition: 'all var(--ios-duration-fast) var(--ios-ease-out)'
              }}
            >
              <Icon name="ExternalLink" size={15} />
              <span>Visit Landing Page</span>
            </a>
          </div>
        </div>
        {/* Statistics Cards */}
        {activeTab === 'assignments' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total', value: assignmentStats.total, icon: 'FileText', color: 'blue' },
                { label: 'Pending', value: assignmentStats.pending, icon: 'Clock', color: 'yellow' },
                { label: 'In Progress', value: assignmentStats.inProgress, icon: 'Play', color: 'blue' },
                { label: 'Completed', value: assignmentStats.completed, icon: 'CheckCircle', color: 'green' },
              ].filter(stat => stat.label === 'Total' || stat.value > 0).map((stat) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="ios-card-secondary"
                  style={{padding: 'var(--ios-space-4)'}}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="ios-text-secondary text-xs uppercase tracking-wide" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-medium)'}}>{stat.label}</p>
                      <p className="text-2xl font-bold ios-text-primary mt-1" style={{fontFamily: 'var(--ios-font-system)', fontWeight: 'var(--ios-weight-bold)'}}>{stat.value}</p>
                    </div>
                    <div className={`w-10 h-10 bg-${stat.color}-500/15 rounded-xl flex items-center justify-center`} style={{borderRadius: 'var(--ios-radius-lg)'}}>
                      <Icon name={stat.icon} size={20} className={`text-${stat.color}-400`} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Table Section */}
            <div className="p-6 rounded-xl bg-transparent border-none shadow-none">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-white mb-2">My Assignments</h2>
                <p className="text-white/60 text-sm">Track your assignments and their progress</p>
              </div>
              
              <ClientAssignmentsTable onMessageClick={handleMessageClick} />
            </div>
          </div>
        )}

        {activeTab === 'classes' && (
          <div className="bg-transparent backdrop-blur-none border-none rounded-xl p-6 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total', value: classStats.total, icon: 'GraduationCap', color: 'purple' },
                { label: 'Pending', value: classStats.pending, icon: 'Clock', color: 'yellow' },
                { label: 'In Progress', value: classStats.inProgress, icon: 'Play', color: 'blue' },
                { label: 'Completed', value: classStats.completed, icon: 'CheckCircle', color: 'green' },
              ].filter(stat => stat.label === 'Total' || stat.value > 0).map((stat) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card p-4 rounded-lg"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/60 text-sm">{stat.label}</p>
                      <p className="text-2xl font-bold text-white">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 bg-${stat.color}-500/20 rounded-lg flex items-center justify-center`}>
                      <Icon name={stat.icon} size={24} className={`text-${stat.color}-400`} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Table Section */}
            <div className="p-6 rounded-xl bg-transparent border-none shadow-none">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-white mb-2">My Classes</h2>
                <p className="text-white/60 text-sm">Track your classes and their progress</p>
              </div>
              
              <ClientClassesTable onMessageClick={handleMessageClick} />
            </div>
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="h-full">
            <MessageCenter />
          </div>
        )}
        
      </div>
        
      {/* Notifications */}
      <NotificationManager />
      
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            zIndex: 99999,
          },
        }}
      />

      {/* Assignment Submission Modal */}
      <AssignmentSubmissionForm
        isOpen={showAssignmentModal}
        onClose={() => setShowAssignmentModal(false)}
      />

      {/* Class Submission Modal */}
      <ClassManagementForm
        isOpen={showClassModal}
        onClose={() => setShowClassModal(false)}
      />

      {/* Discord Connection Panel */}
      <DiscordConnectionPanel
        isOpen={showDiscordPanel}
        onClose={() => setShowDiscordPanel(false)}
      />

      {/* Discord Connection Reminder */}
      <DiscordConnectionReminder
        onConnectClick={() => setShowDiscordPanel(true)}
      />

      {/* Profile Settings Modal */}
      {/* {showProfileSettings && (
        <ProfileSettings
          onClose={() => setShowProfileSettings(false)}
        />
      )} */}
    </ResponsiveSidebar>
    </>
  );
};

export default ClientDashboardPage;